import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { ViewType } from '@rivethealth/data/line-item';
import { AvatarState, Option } from '@rivethealth/data/options';
import { Permissions } from '@rivethealth/data/permissions';
import { Permission } from '@rivethealth/data/user-account';
import * as _ from 'lodash';
import { SavedUser, UserService } from '../api/user.service';
import { AccountState, useAccountId } from '../app/account/account.state';
import { PermissionsState } from '../app/account/permissions-state.service';
import { AnchoredPosition } from '../common/callout/callout-position';
import { Loadable } from '../common/helpers/loadable';
import { HookDirective } from './core/hook';
import { useEffect, useLoad, useState } from './core/hooks';
import { RouteService } from './state/route.service';

@Component({
  selector: 'assignee-dropdown',
  templateUrl: './assignee-dropdown.component.html',
  styleUrls: ['./assignee-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssigneeDropdownComponent extends HookDirective {
  constructor(
    private readonly userService: UserService,
    private readonly account: AccountState,
    cdr: ChangeDetectorRef,
    private readonly permissionsState: PermissionsState,
    private readonly routeService: RouteService,
  ) {
    super(cdr);
  }

  get _() {
    const [menuOpen, _setMenuOpen] = useState<boolean>(false);

    const setMenuOpen = (state: boolean) => {
      _setMenuOpen(state);
      this.openMenuEvent.next(state);
    };

    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

    const accountId = useAccountId(this.routeService);

    const [usersLoadable] = useLoad(
      () =>
        this.userService.getUsers(
          accountId,
          false,
          this.userPermission ? [this.userPermission] : [],
        ),
      [accountId, this.userPermission],
    );
    const users = usersLoadable.data || [];

    if (!this.loadable) {
      this.loadable = usersLoadable;
    }

    const success =
      this.loadable &&
      this.loadable.data != null &&
      !this.loadable.loading &&
      !this.loadable.erred;

    useEffect(() => {
      if (success) {
        setMenuOpen(false);
      }
    }, [success]);

    const sortedUserOptions = _.sortBy(users, user =>
      user.name.toLocaleLowerCase(),
    ).map(
      user =>
        <Option>{
          id: user.id.toString(),
          title: user.name,
          subtitle: null,
          avatarState: this.userAvatars.find(
            avatar => avatar.user.id === user.id,
          )?.avatarState,
        },
    );
    const userOptions = this.allowMultiple
      ? sortedUserOptions
      : [
          { id: 'unassign', title: 'Unassign', subtitle: null },
          ...sortedUserOptions,
        ];

    let assignedUserIds = this.assignedUserIds;
    if (!Array.isArray(this.assignedUserIds) && this.assignedUserIds) {
      assignedUserIds = [this.assignedUserIds];
    }

    const assignedAvatars = this.userAvatars.filter(
      a => !!a && assignedUserIds?.includes(a.user.id),
    );

    useEffect(() => {
      if (assignedAvatars) {
        setSelectedUserIds(assignedAvatars.map(i => i.user.id.toString()));
      }
    }, [assignedAvatars]);

    const sendSelectedUserIds = () => {
      const userIds = selectedUserIds;
      if (this.clearSelection) {
        setSelectedUserIds([]);
      }
      if (userIds.length == 1 && userIds[0] == 'unassign') {
        this.setAssignees.next([]);
      } else {
        this.setAssignees.next(userIds.map(u => parseInt(u)));
      }
    };

    const locked: boolean = this.permission
      ? !this.permissionsState.isAllowed(this.permission) || this.disabled
      : this.disabled;
    const settingsLocked: boolean = this.settingsPermission
      ? !this.permissionsState.isAllowed(this.settingsPermission)
      : false;

    return {
      assignedAvatars,
      assignedUserIds,
      locked,
      settingsLocked,
      menuOpen,
      selectedUserIds,
      setSelectedUserIds,
      sendSelectedUserIds,
      setMenuOpen,
      userOptions,
    };
  }

  @Input()
  permission: Permission | null;

  @Input()
  settingsPermission: Permission | null;

  @Input()
  userAvatars: { user: SavedUser; avatarState: AvatarState }[];

  @Input()
  readOnlyMode: boolean = true;

  @Input()
  icon: boolean;

  @Input()
  compact: boolean;

  @Input()
  isWorklist: boolean = false;

  @Input()
  allowUnassigned: boolean = false;

  @Input()
  allowMultiple: boolean = false;

  @Input()
  userPermission: Permission | null = null;

  accountId = this.account.id;

  readonly AnchoredPosition = AnchoredPosition;

  @Input()
  issueType: Exclude<ViewType, 'claims'> | null = null;

  @Input()
  assignedUserIds: number[] | null;

  @Output()
  setAssignees = new EventEmitter<number[]>();

  @Output()
  openMenuEvent = new EventEmitter<boolean>();

  @Input()
  loadable: Loadable<any>;

  Permissions = Permissions;

  @Input()
  clearSelection: boolean;

  @Input()
  disabled: boolean = false;

  @Input()
  preventDefault: boolean = false;

  cancel() {
    this._.setMenuOpen(false);
    this._.setSelectedUserIds(
      this._.assignedUserIds?.map(u => u.toString()) || [],
    );
  }
}
